import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { CloudUpload, MoreHorizontal, Plus, Trash } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetAllPolicies } from '~entities/policies';
import { useBulkDeletePolicies } from '~entities/policies/api/bulk-delete-policies';
import { useGetAllPoliciesForCSV } from '~entities/policies/api/get-all-policies-for-csv';
import type { PolicyDetails } from '~entities/policies/modal/policy';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { exportToCSV } from '~shared/helpers/csv';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';

import { DeletePolicyDialog } from './delete-policy-dialog';

// Add this helper function at the top of the file
const truncateText = (text: string, limit: number) => {
  if (text.length <= limit) return text;
  return text.slice(0, limit) + '...';
};

// Create a column helper
const columnHelper = createColumnHelper<PolicyDetails>();

export const PoliciesListTable = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data } = useGetAllPolicies();
  // const { data: classesData } = useGetAllClasses();
  // const { data: eventsData } = useGetAllEvents();

  const bulkDeleteMutation = useBulkDeletePolicies();

  const { refetch: getAllPoliciesForCSV } = useGetAllPoliciesForCSV({
    enabled: false,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState<
    Array<PolicyDetails>
  >([]);
  // const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // const deleteMutation = useDeletePolicy();

  const handleExport = async () => {
    try {
      const { data: allPoliciesData } = await getAllPoliciesForCSV();
      const headers = ['Policy Name', 'Description', 'Classes', 'Events'];
      const formattedData =
        allPoliciesData?.map((policy) => ({
          'Policy Name': capitalizeWords(policy.name),
          Description:
            policy.description.length > 0
              ? policy.description.charAt(0).toUpperCase() +
                policy.description.slice(1)
              : '',
          Classes: policy.classes
            ? policy.classes.map(capitalizeWords).join(', ')
            : '',
          Events: policy.events
            ? policy.events.map(capitalizeWords).join(', ')
            : '',
        })) ?? [];
      exportToCSV(
        formattedData,
        headers,
        `policies-export-${new Date().toISOString()}.csv`,
      );
    } catch (error) {
      console.error('Error exporting policies:', error);
    }
  };

  // Move columns definition here to access classesData
  const cols = useMemo(() => {
    return [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <div className='px-4'>
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => {
                table.toggleAllPageRowsSelected(!!value);
              }}
              aria-label='Select all'
            />
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div className='px-4'>
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => {
                  row.toggleSelected(!!value);
                }}
                aria-label='Select row'
              />
            </div>
          );
        },
      }),
      columnHelper.accessor('name', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Name'
          />
        ),
        cell: (info) => (
          <Button variant={'link'} className='text-custom-secondary-1' asChild>
            <Link
              to={`/policies/$policyId`}
              params={{
                policyId: info.row.original._id.toString(),
              }}
            >
              {info.getValue()}
            </Link>
          </Button>
        ),
      }),
      columnHelper.accessor('description', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Description'
          />
        ),
        cell: (info) => {
          const description = info.getValue() || '';
          const capitalizedDescription =
            description.length > 0
              ? description.charAt(0).toUpperCase() + description.slice(1)
              : '';
          return (
            <div className='flex h-11 items-center px-4'>
              {truncateText(capitalizedDescription, 50)}
            </div>
          );
        },
      }),
      columnHelper.accessor('class', {
        header: (info) => {
          return (
            <DataTableColumnHeader
              className='text-black'
              column={info.column}
              title='Class'
            />
          );
        },
        cell: (info) => (
          <div className='px-4'>{info.getValue().length || 0}</div>
        ),
      }),
      columnHelper.accessor('event', {
        header: (info) => (
          <DataTableColumnHeader
            className='text-black'
            column={info.column}
            title='Event'
          />
        ),
        cell: (info) => (
          <div className='px-4'>{info.getValue().length || 0}</div>
        ),
      }),
      columnHelper.accessor('_id', {
        header: () => <div className='pl-4 text-black'>Actions</div>,
        cell: (info) => {
          const policy = info.row.original;
          return (
            <div className='px-4'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' className='size-8 p-0'>
                    <MoreHorizontal className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    className='text-red-600'
                    onClick={() => {
                      setSelectedPolicies([policy]);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      }),
    ];
  }, []);

  return (
    <div className='mb-10 mt-6'>
      <EnrollioTable
        ariaLabel='Policies'
        totalLabel='Policies'
        filterHeader={
          <Input
            placeholder='Search Policies'
            value={
              (columnFilters.find((filter) => filter.id === 'name')?.value ??
                '') as string
            }
            onChange={(event) => {
              setColumnFilters([
                {
                  id: 'name',
                  value: event.target.value,
                },
              ]);
            }}
            className='w-full md:w-72'
          />
        }
        toolbar={(table) => (
          <>
            {Object.keys(rowSelection).length > 0 && (
              <Button
                variant='destructive'
                size='sm'
                onClick={() => {
                  const selectedPolicies = Object.keys(rowSelection)
                    .map((index) => data?.[parseInt(index)])
                    .filter((policy): policy is PolicyDetails => !!policy);
                  setSelectedPolicies(selectedPolicies);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash />
                Delete Selected ({Object.keys(rowSelection).length})
              </Button>
            )}
            <Button
              className='bg-custom-secondary-1 text-white hover:bg-custom-secondary-1'
              asChild
            >
              <Link to='/policies/new'>
                <Plus />
                Add Policy
              </Link>
            </Button>
            <Button variant={'outline'} onClick={handleExport}>
              <CloudUpload />
              Export
            </Button>
            <EnrollioTableColumnToggle table={table} />
          </>
        )}
        columns={cols}
        data={data ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        enableRowSelection={true}
        showSelectionCount
      />

      <DeletePolicyDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedPolicies={selectedPolicies}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedPolicies([]);
          setRowSelection({});
        }}
        isBulkDelete={selectedPolicies.length > 1}
        onDelete={() => {
          bulkDeleteMutation.mutate({
            policyIds: selectedPolicies.flatMap((policy) => policy._id || []),
          });
        }}
      />
    </div>
  );
};
